/**
 * Client-side guard for the admin upload screens.
 *
 * Mirrors UploadFileCommandValidator on the API, which checks the extension, the content
 * type AND the leading magic bytes and accepts nothing else. The API stays the authority —
 * this only spares the operator a round trip that ends in a rejected request, and gives a
 * message that says what to do instead of surfacing a raw server error.
 *
 * Unlike the customer app, PDF is allowed here: an operator attaching a bank statement to
 * a payment is the case the admin console exists for.
 */

/** Matches UploadFileCommandValidator.MaxFileSizeBytes. */
export const MAX_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;

export const ACCEPTED_UPLOAD_TYPES =
  "image/png,image/jpeg,image/jpg,application/pdf";

const ALLOWED_TYPES = ACCEPTED_UPLOAD_TYPES.split(",");

/**
 * iPhones shoot HEIC by default. Safari converts to JPEG when the photo comes from the
 * Photos picker, but a file forwarded from the Files app arrives as HEIC and the API
 * rejects it — so it earns a message that names the cause. Matched on extension too
 * because some browsers report an empty `type` for HEIC.
 */
const HEIC_MIME_TYPES = [
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
];

const isHeicFile = (file: File): boolean =>
  HEIC_MIME_TYPES.includes(file.type.toLowerCase()) || /\.hei[cf]$/i.test(file.name);

const formatMegabytes = (bytes: number): string =>
  (bytes / (1024 * 1024)).toFixed(0);

/**
 * Returns an operator-readable reason the file cannot be uploaded, or null when it passes.
 */
export function validateUploadFile(file: File): string | null {
  if (isHeicFile(file)) {
    return `"${file.name}" is an iPhone HEIC photo, which is not supported. Ask for a JPG or PNG instead.`;
  }

  if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
    return `"${file.name}" is not a supported format. Only PNG, JPG and PDF are allowed.`;
  }

  // Checked after the type so a wrong-format file is never reported as merely too large.
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    return `"${file.name}" is ${formatMegabytes(file.size)}MB. The limit is ${formatMegabytes(MAX_UPLOAD_SIZE_BYTES)}MB per file.`;
  }

  return null;
}

/**
 * Splits a picked selection into the files worth sending and the reasons for the rest.
 * Returning both keeps the caller from dropping files silently — an operator who picked
 * five files and saw three upload would otherwise have no idea what happened to the other
 * two.
 */
export function partitionValidUploads(files: File[]): {
  valid: File[];
  errors: string[];
} {
  const valid: File[] = [];
  const errors: string[] = [];

  for (const file of files) {
    const error = validateUploadFile(file);
    if (error) {
      errors.push(error);
    } else {
      valid.push(file);
    }
  }

  return { valid, errors };
}
